/**
 * useWizardStepEntry / useWizardStepRefresh — wizard step-entry plumbing.
 *
 * Why these exist
 * ---------------
 * Wizard steps lazy-mount on first visit (SetupWizardPage's `visitedSteps`)
 * and then stay mounted forever behind `display: none`. A step therefore
 * never re-runs its mount effects on return, and never notices that an
 * EARLIER step changed the data it already fetched. StepLiftsFloors hit this
 * first: the operator changed Rear Doors on Banks, walked forward to Lifts &
 * Floor, and read the pre-edit bank. Its fix was a local `prevStepRef`
 * effect. This module is that shape, extracted, plus the version-gated
 * variant the wizard's field-invalidation rules (utils/wizardInvalidation.ts)
 * now make possible.
 *
 * useWizardStepEntry — the primitive
 * ----------------------------------
 * Calls `onEnter` on each transition INTO `step`. Not on mount, and not while
 * already on the step. Use it when the step's data can go stale for reasons
 * the invalidation rules do not (and should not) model — e.g. a cosmetic
 * rename in an earlier step that the rule tables deliberately treat as a
 * no-op change.
 *
 * useWizardStepRefresh — the gated variant
 * ----------------------------------------
 * Calls `refetch` on entry into `step`, but only when the step has been
 * invalidated since this hook last satisfied it. Reads
 * `WizardState.stepInvalidationVersions`, which `unmarkStepCompleted` bumps
 * for every step it unmarks. Use it when the invalidation rule table already
 * knows the step's data is stale — the refetch is then free of pointless
 * round-trips on entries after an unrelated navigation.
 *
 * Two existing effects were surveyed as candidates when this module was
 * introduced and both were found to need the UNCONDITIONAL primitive
 * instead, for reasons specific to each: `StepLiftsFloors.tsx`
 * (migrated onto `useWizardStepEntry` in this change) refetches on every
 * entry because that guarantee is documented flatly in the wizard book and
 * should not become newly contingent on rule-table completeness in the same
 * change that edits the rule table; `StepBanks.tsx`'s own channel-refetch
 * effect (its `if (currentStep !== WizardStep.Banks...) fetchChannels()`
 * guard, left untouched) exists specifically because a channel *rename*
 * invalidates nothing by rule-table design
 * (`CHANNEL_FIELD_RULES.name: []`), so a version-gated hook would never
 * catch it at all.
 *
 * `useWizardStepRefresh` is consumed by `StepDebounce`, which re-reads its
 * selected bank's record on an invalidated re-entry: `BANK_FIELD_RULES
 * .channelId`/`.adapterId` and `CHANNEL_FIELD_RULES`/`ADAPTER_FIELD_RULES
 * .serverId`/`.organizationId` all name `WizardStep.Debounce`, and without a
 * refetch the debounce-overrides grid stayed built from the pre-edit bank
 * record — most concretely, from the wrong adapter's message-key vocabulary
 * after an `adapterId` change. See the comment on StepDebounce's
 * `useWizardStepRefresh` call for the mechanism.
 *
 * `StepBacnet` was surveyed as a candidate too and deliberately does NOT use
 * this hook: its `cdoValue`/`bankMappings`/`siteName`/`siteId` are not just
 * React state but the literal payload of its own `useWizardStepDraft` —
 * auto-saved, potentially large, unsaved operator work. A refresh-triggered
 * reload there would silently overwrite the persisted draft, not just
 * in-memory state, breaking the book's promise that leaving the step does
 * not lose it. See the comment on StepBacnet's `loadAdapter`-triggering
 * effect for the full reasoning and the residual gap this leaves open.
 *
 * "Invalidated while already on the step" is deferred to the next entry
 * ---------------------------------------------------------------------
 * Neither hook fires while the operator is standing on the step, even though
 * an invalidation can land there (e.g. a detail-drawer edit unmarking the
 * step currently being viewed). Refetching under the cursor would overwrite
 * a half-filled form — the same class of loss `useWizardStepDraft` exists to
 * prevent. Nothing is dropped by deferring: the version counter is
 * monotonic, so the watermark comparison still fails on the next entry and
 * the refetch happens then.
 *
 * Mount parity
 * ------------
 * On mount both hooks record the current step (and, for the gated variant,
 * the current version) as already seen, and fire nothing. A step mounts
 * because the operator just navigated to it for the first time, and its own
 * mount-time hydration effects fetch current data; an extra refetch here
 * would be a duplicate round-trip on every step's first visit. Any
 * invalidation that landed before the step ever mounted is likewise moot —
 * the mount fetch got current data regardless.
 *
 * Because both hooks carry their watermarks in refs rather than state,
 * React's StrictMode double-invoked effects are handled by construction:
 * the second invoke sees the watermark the first one wrote and returns early.
 *
 * A note on `step`, because this codebase has more than one numbering
 * -------------------------------------------------------------------
 * `step` here is the `WizardStep` index (0-13) — the same value as
 * `state.currentStep`, and the same numbering `useWizardStepDraft`'s own
 * `step` parameter uses. It is NOT a backend stage number — do not pass
 * anything other than a `WizardStep` member.
 */

import { useEffect, useRef } from "react";

import { useWizard } from "../WizardContext";

export interface UseWizardStepEntryOptions {
  /** WizardStep index to watch. See the numbering note above. */
  step: number;
  /** Run on each transition into `step`. Held in a ref, so an inline arrow
   *  at the call site is fine and does not re-arm the effect. */
  onEnter: () => void;
  /** Default true. When false the hook keeps tracking navigation but never
   *  calls `onEnter` — for a step rendered standalone outside the wizard
   *  flow. */
  enabled?: boolean;
}

export function useWizardStepEntry(options: UseWizardStepEntryOptions): void {
  const { step, onEnter, enabled = true } = options;
  const { state } = useWizard();
  const { currentStep } = state;

  const onEnterRef = useRef(onEnter);
  onEnterRef.current = onEnter;

  const prevStepRef = useRef(currentStep);

  useEffect(() => {
    const wasOnStep = prevStepRef.current === step;
    // Advance BEFORE any early return — the tracker must never fall behind
    // real navigation regardless of `enabled`, or re-enabling later can fire
    // a spurious call for a transition that already happened while disabled.
    prevStepRef.current = currentStep;
    if (!enabled) return;
    if (currentStep !== step || wasOnStep) return;
    onEnterRef.current();
  }, [currentStep, step, enabled]);
}

export interface UseWizardStepRefreshOptions {
  /** WizardStep index to watch. See the numbering note above. */
  step: number;
  /** Run on entry into `step` when it has been invalidated since this hook
   *  last satisfied it. Held in a ref; inline arrows are fine. */
  refetch: () => void;
  /** Default true. When false, `useWizardStepEntry` tracks navigation but
   *  never calls `onEnter`, so no refetch can fire while disabled — but the
   *  watermark itself is left untouched. Any version bump that lands while
   *  disabled (or while enabled but deferred, per the docs above) is still
   *  honored on the next genuine entry after re-enabling. Fast-forwarding
   *  the watermark on disable was tried and removed: it could not
   *  distinguish "invalidated while genuinely not watching" from
   *  "invalidated while on-step and already deferred, then disabled before
   *  the next entry" — collapsing both to "discard" silently dropped the
   *  second, real case. */
  enabled?: boolean;
}

export function useWizardStepRefresh(options: UseWizardStepRefreshOptions): void {
  const { step, refetch, enabled = true } = options;
  const { getStepVersion } = useWizard();

  // Defensive optional-call, not just a plain getStepVersion(step). Several
  // step test files mock ../../WizardContext with an untyped partial
  // `useWizard` factory; jest.mock factories are not type-checked against
  // WizardContextValue, so a mock written before this accessor existed will
  // keep omitting it. `?.()` degrades that case to "never invalidated"
  // instead of throwing "getStepVersion is not a function". This is a
  // safety net for test mocks — the real WizardContextValue accessor stays
  // non-optional so the provider is compiler-forced to supply it.
  const currentVersion = getStepVersion?.(step) ?? 0;

  // Watermark: the version this hook has already acted on. A ref, not state
  // — writing it must not re-render, and it must be readable synchronously
  // inside the entry callback.
  const seenVersionRef = useRef(currentVersion);

  // `currentVersion` must never appear in useWizardStepEntry's effect
  // dependency array (that's its job to guarantee, not this hook's — a bump
  // landing while the operator is still on the step must defer to the next
  // entry, not refire immediately). No separate "live version" ref is needed
  // to read it safely from onEnter below: useWizardStepEntry refreshes its
  // own onEnter ref unconditionally on every render (before any effect
  // runs), so by the time onEnter is ever invoked, it is always this
  // render's closure — which already closes over this render's
  // currentVersion via ordinary JS scoping.
  useWizardStepEntry({
    step,
    enabled,
    onEnter: () => {
      // Inequality, never `>`. The version map resets to {} on hydration and
      // on organization delete; a mounted step holding a higher watermark
      // must still treat that reset as a change and refetch once.
      if (seenVersionRef.current === currentVersion) return;
      seenVersionRef.current = currentVersion;
      refetch();
    },
  });
}
